import { msg } from "@lingui/core/macro";
import type { MessageDescriptor } from "@lingui/core";

/**
 * The SMB landing's content — ported from Jarvis's prototype
 * (squidler.io/apps/jarvis.squidler/landningssida-prototyp, copy.md v5,
 * 2026-10-01). Structure here, rendering in the sibling components, so the
 * copy can be reviewed in one place and the data invariants tested
 * (content.test.ts).
 *
 * Customer facts carry a `pending` marker where the prototype had a BEKRÄFTA /
 * CITAT SAKNAS / BILD flag: the figure or quote exists in a source but the
 * customer has not approved publication. ONLY THE LABEL IS KEPT HERE. The
 * prototype's reviewer notes (meeting sources, transcripts, who said what)
 * stay in Jarvis's copy.md and must never enter this file: everything in it
 * reaches the client bundle, and the page is reachable on prod through the
 * experiment override. Markers come out one by one as approvals land, and the
 * experiment must not get traffic while any remain.
 */

export type PendingLabel = "BEKRÄFTA" | "CITAT SAKNAS" | "BILD";

export interface Pending {
  readonly label: PendingLabel;
}

/** Generated illustration, not the customer; replaced by a real photo on approval. */
const PLACEHOLDER_PHOTO: Pending = { label: "BILD" };

/* ---- Why: the pile of jobs ---- */

export interface PileWord {
  readonly label: MessageDescriptor;
  /** Lifted away once the pile has landed — what the agent takes. The three
   *  that stay (quotes, contracts, supplier calls) are what still needs you. */
  readonly lifted: boolean;
  /** Left out on narrow screens, where fifteen chips would not fit. */
  readonly narrowHidden: boolean;
  /** Resting tilt, degrees. */
  readonly tilt: number;
}

export const PILE_WORDS: readonly PileWord[] = [
  { label: msg`Invoices`, lifted: true, narrowHidden: false, tilt: -2 },
  { label: msg`Newsletters`, lifted: true, narrowHidden: true, tilt: 1.5 },
  { label: msg`Bookkeeping`, lifted: true, narrowHidden: false, tilt: -1 },
  { label: msg`Contracts`, lifted: false, narrowHidden: false, tilt: 2 },
  { label: msg`Scheduling`, lifted: true, narrowHidden: true, tilt: -2.5 },
  { label: msg`Receipts`, lifted: true, narrowHidden: false, tilt: 1 },
  { label: msg`Supplier calls`, lifted: false, narrowHidden: true, tilt: -1.5 },
  { label: msg`Chasing payments`, lifted: true, narrowHidden: false, tilt: 2.5 },
  { label: msg`Replies`, lifted: true, narrowHidden: false, tilt: -1 },
  { label: msg`Reports`, lifted: true, narrowHidden: true, tilt: 1.5 },
  { label: msg`Quotes`, lifted: false, narrowHidden: false, tilt: -2 },
  { label: msg`Bookings`, lifted: true, narrowHidden: false, tilt: 1 },
  { label: msg`Reminders`, lifted: true, narrowHidden: true, tilt: -1.5 },
  { label: msg`Follow-ups`, lifted: true, narrowHidden: false, tilt: 2 },
  { label: msg`Payroll`, lifted: true, narrowHidden: true, tilt: -1 },
];

export const WHY_OPTIONS: readonly { lead: MessageDescriptor; text: MessageDescriptor }[] = [
  { lead: msg`Do it yourself.`, text: msg`Your best hours go to the screen.` },
  { lead: msg`Ask ChatGPT.`, text: msg`It answers, and you still do the work.` },
  { lead: msg`Hire someone.`, text: msg`You pay a salary, then spend weeks training them.` },
];

/* ---- What it does: the hub of tools and the job rows ---- */

export interface HubTool {
  readonly key: string;
  readonly name: MessageDescriptor;
  /** File under public/logos. Tools we have no logo for show `letter`. */
  readonly logo?: string;
  readonly letter?: string;
  /** Brand colour behind the letter. */
  readonly color?: string;
  /** The dashed "+ any API" node. */
  readonly api?: boolean;
}

/** Fourteen nodes, alternating outer/inner ring so the labels don't collide. */
export const HUB_TOOLS: readonly HubTool[] = [
  { key: "gmail", name: msg`Gmail`, logo: "gmail.svg" },
  { key: "fortnox", name: msg`Fortnox`, logo: "fortnox.svg" },
  { key: "cal", name: msg`Calendar`, logo: "google-calendar.svg" },
  { key: "teams", name: msg`Teams`, logo: "microsoft-teams.svg" },
  { key: "drive", name: msg`Drive`, logo: "google-drive.svg" },
  { key: "bokio", name: msg`Bokio`, logo: "bokio.svg" },
  { key: "slack", name: msg`Slack`, logo: "slack.svg" },
  { key: "pipedrive", name: msg`Pipedrive`, logo: "pipedrive.png" },
  { key: "outlook", name: msg`Outlook`, letter: "O", color: "#0F6CBD" },
  { key: "vitec", name: msg`Vitec`, logo: "vitec.svg" },
  { key: "sheets", name: msg`Sheets`, logo: "google-sheets.svg" },
  { key: "m365", name: msg`Microsoft 365`, letter: "M", color: "#D83B01" },
  { key: "telegram", name: msg`Telegram`, logo: "telegram.svg" },
  { key: "api", name: msg`+ any API`, api: true },
];

export interface JobRow {
  readonly key: string;
  /** HUB_TOOLS keys lit while this row is the one being read. */
  readonly tools: readonly string[];
  readonly title: MessageDescriptor;
  readonly text: MessageDescriptor;
  /** Looping clip beside the row. The first row shows the inbox mock instead. */
  readonly clip?: { readonly video: string; readonly poster: string };
}

/** Generated placeholder clips (Veo), to be replaced by real customer footage
 *  — see copy.md "Bilder att ersätta". */
const clip = (name: string) => ({
  video: `/landing/smb/rows/${name}.mp4`,
  poster: `/landing/smb/rows/${name}-poster.jpg`,
});

export const JOB_ROWS: readonly JobRow[] = [
  {
    key: "inbox",
    tools: ["gmail", "outlook", "slack", "teams", "telegram"],
    title: msg`Get your inbox handled without reading all of it yourself.`,
    text: msg`Out of forty new emails, you open three. It drafts the replies and files the rest. When someone goes quiet, it follows up without being asked.`,
  },
  {
    key: "customers",
    tools: ["pipedrive", "gmail", "outlook"],
    title: msg`Get new customers found without hunting for them yourself.`,
    text: msg`It finds companies that fit and drafts the first message. You decide who gets it.`,
    clip: clip("customers"),
  },
  {
    key: "meetings",
    tools: ["cal", "outlook", "teams"],
    title: msg`Get meetings booked without booking them yourself.`,
    text: msg`It sends the invite, and when plans change it moves things around.`,
    clip: clip("meetings"),
  },
  {
    key: "books",
    tools: ["fortnox", "bokio", "sheets"],
    title: msg`Get the books kept without keeping them yourself.`,
    text: msg`It chases the receipts, so the numbers are there when you need them.`,
    clip: clip("books"),
  },
  {
    key: "research",
    tools: ["drive", "m365", "sheets"],
    title: msg`Get the market researched without doing the digging yourself.`,
    text: msg`It comes back with an answer and its sources.`,
    clip: clip("research"),
  },
  {
    key: "competitors",
    tools: ["vitec", "api"],
    title: msg`Hear when competitors move without watching them yourself.`,
    text: msg`Name the companies. It tells you when something changes.`,
    clip: clip("competitors"),
  },
];

export type InboxTone = "need" | "draft" | "done" | "filed";

export interface InboxRow {
  readonly from: MessageDescriptor;
  readonly subject: MessageDescriptor;
  readonly status: MessageDescriptor;
  readonly tone: InboxTone;
}

export const INBOX_ROWS: readonly InboxRow[] = [
  { from: msg`Supplier`, subject: msg`New prices`, status: msg`Needs you`, tone: "need" },
  { from: msg`New customer`, subject: msg`Time next week?`, status: msg`Reply drafted`, tone: "draft" },
  { from: msg`Regular`, subject: msg`Move Thursday?`, status: msg`Moved, confirmed`, tone: "done" },
  { from: msg`Accountant`, subject: msg`The August receipt`, status: msg`Found, sent`, tone: "done" },
  { from: msg`Newsletters`, subject: msg`12 more`, status: msg`Filed`, tone: "filed" },
];

/* ---- Over time: the memory that grows ---- */

/** Made-up examples, no customer facts. */
export const MEMORY_FACTS: readonly MessageDescriptor[] = [
  msg`Invoices go out on the 25th`,
  msg`Anna would rather get a call`,
  msg`No meetings before nine`,
  msg`Swedish customers get replies in Swedish`,
  msg`The supplier raised prices in March`,
  msg`Friday afternoons are for the workshop`,
];

/* ---- At work: the four customer cases ---- */

export interface CustomerCase {
  readonly key: string;
  /** The first card is the dark one. */
  readonly dark?: boolean;
  readonly photo: { readonly src: string; readonly alt: MessageDescriptor; readonly pending: Pending };
  readonly tag: MessageDescriptor;
  readonly title: MessageDescriptor;
  readonly titlePending?: Pending;
  readonly figure?: { readonly value: string; readonly label: MessageDescriptor; readonly pending?: Pending };
  readonly agents?: readonly MessageDescriptor[];
  readonly text: MessageDescriptor;
  readonly quote?: MessageDescriptor;
  readonly who: { readonly name: string; readonly role: MessageDescriptor; readonly pending: Pending };
  readonly more?: { readonly text: MessageDescriptor; readonly pending?: Pending };
}

export const CASES: readonly CustomerCase[] = [
  {
    key: "factory",
    dark: true,
    photo: {
      src: "/landing/smb/cases/factory.jpg",
      alt: msg`Illustrative photo: a plant manager on a factory floor`,
      pending: PLACEHOLDER_PHOTO,
    },
    tag: msg`The factory`,
    title: msg`The morning meeting runs on Neo's report.`,
    figure: {
      value: "182",
      label: msg`readers of the daily report`,
      pending: { label: "BEKRÄFTA" },
    },
    text: msg`Neo is Robert Jingborg's agent. The production report it writes goes up on the board every day.`,
    quote: msg`Neo has very quickly become my most important and best colleague!`,
    who: {
      name: "Robert Jingborg",
      role: msg`plant manager, JELD-WEN`,
      pending: { label: "BEKRÄFTA" },
    },
    more: {
      text: msg`Balancing a production line used to take two weeks with a stopwatch and a notepad. This time someone filmed ten short clips of the line and gave them to Neo. The analysis was done in about an hour.`,
      pending: { label: "BEKRÄFTA" },
    },
  },
  {
    key: "educator",
    photo: {
      src: "/landing/smb/cases/educator.jpg",
      alt: msg`Illustrative photo: a hairdressing educator in a training studio`,
      pending: PLACEHOLDER_PHOTO,
    },
    tag: msg`The educator`,
    title: msg`Two founders who chose not to hire.`,
    titlePending: { label: "BEKRÄFTA" },
    figure: { value: "4", label: msg`agents on the team` },
    agents: [msg`IT`, msg`Marketing`, msg`Course material`, msg`Books`],
    text: msg`The agents hand work to each other.`,
    quote: msg`We can't afford to hire for every part. And anyone we hire ends up on my desk anyway. I kept saying everything should run on AI. I just didn't know how.`,
    who: {
      name: "Olivia",
      role: msg`founder, Hairucation`,
      pending: { label: "BEKRÄFTA" },
    },
  },
  {
    key: "goldsmith",
    photo: {
      src: "/landing/smb/cases/goldsmith.jpg",
      alt: msg`Illustrative photo: a goldsmith behind the counter of her workshop`,
      pending: PLACEHOLDER_PHOTO,
    },
    tag: msg`The goldsmith`,
    title: msg`Pen and paper, replaced on day one.`,
    titlePending: { label: "BEKRÄFTA" },
    figure: {
      value: "7×",
      label: msg`more messages to her agent in week 6 than in week 1`,
      pending: { label: "BEKRÄFTA" },
    },
    text: msg`Rebecca Falck's agent built her a till for the gold she buys over the counter.`,
    who: {
      name: "Rebecca Falck",
      role: msg`goldsmith, Helsingborg`,
      pending: { label: "CITAT SAKNAS" },
    },
  },
  {
    key: "startup",
    photo: {
      src: "/landing/smb/cases/startup.jpg",
      alt: msg`Illustrative photo: a startup founder between meetings`,
      pending: PLACEHOLDER_PHOTO,
    },
    tag: msg`The startup`,
    title: msg`The CEO no longer does the numbers by hand.`,
    text: msg`Philippe Höij's agent does DFRNT's cash flow and P&L straight from Fortnox.`,
    quote: msg`The agent now acts as controller and keeps an eye on the KPIs continuously!`,
    who: {
      name: "Philippe Höij",
      role: msg`co-founder and CEO, DFRNT`,
      pending: { label: "BEKRÄFTA" },
    },
    more: {
      text: msg`The balances are pro forma, in euros. When he's on the move, he talks to it in Slack.`,
    },
  },
];

/**
 * Whether the "At work" section (the four customer cases) renders at all.
 * Off since 2026-10-06: the customers' approvals and real photos are still
 * pending, and the page should be tried — possibly made the default — before
 * they land. Everything below stays in place; flipping this shows it again.
 */
export const SHOW_CUSTOMER_CASES: boolean = false;

/**
 * Whether any customer fact still awaits approval AND would be shown. The
 * review notice renders while this is true, and content.test.ts refuses to
 * let the `smb` arm have a bucket range while it is — the gate is code, not a
 * reminder. With the cases hidden nothing unapproved reaches a visitor, so
 * the gate is open.
 */
export const hasPendingMarkers = (): boolean =>
  SHOW_CUSTOMER_CASES &&
  CASES.some(
    (c) =>
      c.who.pending !== undefined ||
      c.photo.pending !== undefined ||
      c.titlePending !== undefined ||
      c.figure?.pending !== undefined ||
      c.more?.pending !== undefined,
  );

/* ---- Your data ---- */

export type TrustIconKey = "globe" | "slash" | "shield" | "people";

export const TRUST_POINTS: readonly { icon: TrustIconKey; title: MessageDescriptor; text: MessageDescriptor }[] = [
  {
    icon: "globe",
    title: msg`Stored in the EU`,
    text: msg`That covers your agent's work and everything it saves along the way.`,
  },
  {
    icon: "slash",
    title: msg`Never used to train AI`,
    text: msg`Nothing you hand over is used to train AI, or for anything but your work.`,
  },
  {
    icon: "shield",
    title: msg`The AI can stay in the EU too`,
    text: msg`If you want, all of your agent's AI work is done inside the EU.`,
  },
  {
    icon: "people",
    title: msg`Your team can join`,
    text: msg`Start on your own. Only the people you invite get in.`,
  },
];

/** "Have a coffee with Mattias" — the closing section's secondary CTA. */
export const COFFEE_URL = "https://calendly.com/m-squidler/squidler-coffee";
